import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  useToast,
  Checkbox,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { User, UserRole } from "../../../../entity/User";
import { WSAuthentication } from "../../../../services/remote/authentication/WSAuthentication";

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  userToEdit: User | null;
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userToEdit,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [role, setRole] = useState<UserRole>(UserRole.USER);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (userToEdit) {
      setEmail(userToEdit.email);
      setRole(userToEdit.role);
      setPassword("");
    } else {
      setEmail("");
      setRole(UserRole.USER);
      setPassword("");
    }
  }, [userToEdit]);

  const handleSubmit = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Email is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!userToEdit && !password) {
      toast({
        title: "Error",
        description: "Password is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const authService = WSAuthentication.getInstance();
      let user: User;

      if (userToEdit) {
        user = await authService.resetUserPassword(userToEdit.email, password);
      } else {
        user = await authService.register({ email, password, role });
      }

      onSuccess(user!!);
      onClose();
    } catch (error) {
      console.error("Failed to save user:", error);
      toast({
        title: "Error",
        description: "Failed to save user",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {userToEdit ? "Edit User" : "Create New User"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Password</FormLabel>
              {userToEdit ? (
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              ) : (
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  isRequired
                />
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Role</FormLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
              >
                <option value={UserRole.ADMIN}>Admin</option>
                <option value={UserRole.SUBADMIN}>Sub Admin</option>
                <option value={UserRole.STORE_ADMIN}>Store Admin</option>
                <option value={UserRole.STORE_EDITOR}>Store Editor</option>
                <option value={UserRole.USER}>User</option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Saving..."
          >
            {userToEdit ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
